import { Button, Center, Group, Loader, Paper, Stack, Text, Textarea, Title } from '@mantine/core';
import { notify } from '@/lib/notify';
import { IconRotate } from '@tabler/icons-react';
import { useMedplum } from '@medplum/react-hooks';
import { useCallback, useEffect, useState } from 'react';

import { useRoleFlags } from '@/hooks/useRbac';
import { DEFAULT_AI_TRANSFER_SUMMARY_SYSTEM_PROMPT } from '@/lib/clinicalSettingsConstants';
import { loadTransferSummaryPrompt, saveTransferSummaryPrompt } from '@/lib/clinicalSettingsService';

export function TransferSummaryPromptSettings(): JSX.Element {
  const medplum = useMedplum();
  const { isAdmin } = useRoleFlags();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [prompt, setPrompt] = useState(DEFAULT_AI_TRANSFER_SUMMARY_SYSTEM_PROMPT);
  const [savedPrompt, setSavedPrompt] = useState(DEFAULT_AI_TRANSFER_SUMMARY_SYSTEM_PROMPT);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const result = await loadTransferSummaryPrompt(medplum);
      setOrganizationId(result.organizationId);
      setPrompt(result.prompt);
      setSavedPrompt(result.prompt);
    } catch (err) {
      notify({
        title: 'Failed to load clinical settings',
        error: err,
        message: 'Unknown error',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [medplum]);

  useEffect(() => {
    void loadSettings();
  }, [loadSettings]);

  const isDirty = prompt !== savedPrompt;

  const handleSave = async (): Promise<void> => {
    if (!organizationId) {
      notify({
        title: 'Save failed',
        message: 'No practice organization is linked to your account.',
        type: 'error',
      });
      return;
    }

    setSaving(true);
    try {
      await saveTransferSummaryPrompt(medplum, organizationId, prompt);
      const refreshed = await loadTransferSummaryPrompt(medplum);
      setPrompt(refreshed.prompt);
      setSavedPrompt(refreshed.prompt);
      notify({
        title: 'Transfer summary prompt saved',
        message: 'AI transfer summaries will use this prompt going forward.',
        type: 'success',
      });
    } catch (err) {
      notify({
        title: 'Save failed',
        error: err,
        message: 'Unknown error',
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = (): void => {
    setPrompt(DEFAULT_AI_TRANSFER_SUMMARY_SYSTEM_PROMPT);
  };

  if (loading) {
    return (
      <Center py="xl">
        <Loader size="sm" />
      </Center>
    );
  }

  return (
    <Paper p="xl" withBorder>
      <Stack gap="md">
        <div>
          <Title order={4} mb={4}>
            AI Transfer Summary Prompt
          </Title>
          <Text size="sm" c="dimmed">
            System prompt used when generating AI transfer handoff summaries from signed clinical history.
            Changes apply to all transfer summary requests for this practice.
          </Text>
        </div>

        <Textarea
          label="System prompt"
          description={
            isAdmin
              ? 'Edit the instructions sent to the AI model. The clinical source data is appended separately at generation time.'
              : 'Read-only. Contact a practice admin to edit this prompt.'
          }
          value={prompt}
          onChange={(event) => setPrompt(event.currentTarget.value)}
          minRows={16}
          autosize
          readOnly={!isAdmin}
          styles={{
            input: { fontFamily: 'monospace', fontSize: 12, resize: 'vertical' },
          }}
        />

        {isAdmin && (
          <Group justify="space-between">
            <Button
              variant="subtle"
              color="gray"
              leftSection={<IconRotate size={14} />}
              onClick={handleReset}
              disabled={prompt === DEFAULT_AI_TRANSFER_SUMMARY_SYSTEM_PROMPT}
            >
              Reset to default
            </Button>
            <Button onClick={() => void handleSave()} loading={saving} disabled={!isDirty || !organizationId}>
              Save prompt
            </Button>
          </Group>
        )}
      </Stack>
    </Paper>
  );
}
